/**
 * Типы для игры лабиринт - адаптированы под универсальную архитектуру
 */

import { GameDifficulty, BaseGameSettings } from '@/contexts/games/types';

// Curved wall segment using Bezier curves
export interface WallSegment {
  type: 'line' | 'bezier' | 'arc';
  start: Position;
  end: Position;
  control1?: Position; // For bezier curves
  control2?: Position; // For cubic bezier curves
  radius?: number; // For arcs
}

// Интерфейс для ячейки лабиринта (legacy - orthogonal mazes)
export interface Cell {
  x: number;
  y: number;
  walls: {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
  };
  visited: boolean;
  isEntrance?: boolean;
  isExit?: boolean;
  isEntranceZone?: boolean;
  isExitZone?: boolean;
}

// Интерфейс для позиций
export interface Position {
  x: number;
  y: number;
}

// Интерфейс для сегментов пути
export interface PathSegment {
  points: Position[];
  color: string;
}

// Интерфейс для размеров канваса
export interface CanvasSize {
  width: number;
  height: number;
}

// Print configuration
export interface PrintConfig {
  paperSize: 'A4' | 'Letter' | 'A3';
  orientation: 'portrait' | 'landscape';
  includeZones: boolean; // Include entrance/exit visual indicators
  includePath: boolean; // Include user's solution path
  includeQRCode: boolean; // Include QR code with link to maze game
  margin: number; // Margin in mm
}

// Интерфейс для параметров сложности
export interface DifficultySettings {
  cellSize: number;
  entranceZoneSize: number;
  exitZoneSize: number;
  pathComplexity: number;
  deadEndDensity: number;
  branchingFactor: number;
  minPathLength: number;
  wallRemovalChance: number;
  shapeStyle: 'orthogonal' | 'organic' | 'circular';
  curvature: number;
}

// Интерфейс для доступных цветов
export interface AvailableColors {
  [key: string]: string;
}

// Типы зон в лабиринте
export enum ZoneType {
  ENTRANCE = 'entrance',
  EXIT = 'exit'
}

// Интерфейс для зоны (входа или выхода)
export interface Zone {
  topLeft: Position;
  size: number;
  entranceCell?: Position;
  type: ZoneType;
  image?: string;
}

// Available maze generation modes
export type MazeMode = 'rectangular' | 'radial' | 'image' | 'template';

// Расширенные настройки игры лабиринт
export interface MazeGameSettings extends BaseGameSettings {
  lineColor: string;
  showGrid: boolean;
  animationSpeed: number;
  shapeStyle: 'orthogonal' | 'organic' | 'circular';
  curvature: number;
  mazeMode: MazeMode;
  /** Opacity of the source photo under a photo maze (0 = hidden … 1 = full). */
  imageOpacity: number;
}

// Состояние лабиринта
export interface MazeState {
  maze: Cell[][];
  rows: number;
  cols: number;
  cellSize: number;
  canvasSize: CanvasSize;
  start: Position;
  end: Position;
  entranceZone: Zone | null;
  exitZone: Zone | null;
  pathSegments: PathSegment[];
  isCompleted: boolean;
  isDarkMode?: boolean;
  loadedImages?: Map<string, HTMLImageElement>;
  wallSegments?: WallSegment[]; // Curved wall segments for rendering
  curvature?: number; // Current curvature setting
}

// Cell in a radial (polar) maze
export interface RadialCell {
  ring: number;
  sector: number;
  walls: {
    inward: boolean;  // wall toward centre
    outward: boolean; // wall toward edge
    cw: boolean;      // clockwise radial wall
    ccw: boolean;     // counter-clockwise radial wall
  };
  visited: boolean;
}

// State for a radial maze
export interface RadialMazeState {
  rings: number;                       // total ring count (including centre)
  sectorsPerRing: number[];            // number of sectors in each ring
  cells: RadialCell[][];               // cells[ring][sector]
  center: Position;                    // canvas centre
  ringWidth: number;                   // pixel width of each ring
  canvasSize: CanvasSize;
  entrance: { ring: number; sector: number };
  exit: { ring: number; sector: number };
  pathSegments: PathSegment[];
  isCompleted: boolean;
  isDarkMode?: boolean;
}

// Voronoi region in an image-based maze
export interface VoronoiRegion {
  id: number;
  /** Polygon vertices defining the region boundary (ordered) */
  vertices: Position[];
  /** Seed point (centroid) of the Voronoi cell */
  site: Position;
  /** IDs of adjacent regions with passages (DFS-carved, walls removed) */
  neighbors: number[];
  /** IDs of ALL geometric neighbors (full Voronoi adjacency, for client re-carving) */
  allNeighbors?: number[];
}

// Data for a Voronoi-based image maze
export interface VoronoiMazeData {
  regions: VoronoiRegion[];
  /** Region ID where the maze entrance is located (on silhouette border) */
  entryRegionId: number;
  /** Region ID where the maze goal is located (near centre of mass) */
  exitRegionId: number;
  /** Silhouette contour polygon for outline rendering (ordered points) */
  contour?: Position[];
  /** Width/height ratio of the original image (for non-square canvas sizing) */
  aspectRatio?: number;
  /** Client-only: the affine map normalized→canvas applied by fitGeometryToBox
   *  (lets the renderer place the source photo under the same mapping). */
  geometryTransform?: { scale: number; ox: number; oy: number };
}

// State for a Voronoi image maze
export interface VoronoiMazeState {
  mazeData: VoronoiMazeData;
  canvasSize: CanvasSize;
  pathSegments: PathSegment[];
  isCompleted: boolean;
  isDarkMode?: boolean;
  /** Source image URL for semi-transparent background overlay */
  backgroundImageUrl?: string;
  /** Preloaded HTMLImageElement for background rendering (client-side only) */
  backgroundImage?: HTMLImageElement;
  /** Opacity for `backgroundImage` (default 0.3). */
  backgroundOpacity?: number;
}

// Template category for maze shape templates
export type MazeTemplateCategory = 'animals' | 'transport' | 'nature' | 'fantasy';

// Metadata for a bundled SVG maze template
export interface MazeTemplate {
  id: string;
  name: string;
  category: MazeTemplateCategory;
  svgPath: string;
  thumbnail: string;
  /** Emoji icon displayed as thumbnail in the template gallery. */
  emoji?: string;
  /** Path to pre-computed VoronoiMazeData JSON (skips queue-based generation). */
  configPath?: string;
}

// Статистика игры лабиринт
export interface MazeStatistics {
  completionTime: number;
  pathLength: number;
  segmentsUsed: number;
  difficulty: GameDifficulty;
  hintsUsed: number;
}


